import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Layout from "../central/Layout";
import { PrimaryButton, SecondaryButton } from "../central/Button";
import InstallBanner from "../components/InstallBanner";
import styled from "styled-components";

// Optional (set later if you want to link a PDF deck)
const MARKETING_DECK_URL = process.env.REACT_APP_MARKETING_DECK_URL || "";
const SETUP_FORM_URL = process.env.REACT_APP_SETUP_FORM_URL || "";

const PageContainer = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.large};
  min-height: 80vh;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.medium};
  }
`;

const Hero = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  border: 1px solid ${({ theme }) => theme.colors.border || "#e6e6e6"};
  border-radius: 14px;
  background: #fff;
  margin-bottom: 0;
`;

const HeroTitle = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
  font-size: 2rem;
  line-height: 1.15;
`;

const HeroSubtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary || "#666"};
  line-height: 1.45;
`;

const CTA = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.medium};
`;

const SetupNudge = styled.a`
  display: block;
  margin-top: ${({ theme }) => theme.spacing.medium};
  font-size: 0.82rem;
  color: #888888 !important;
  text-decoration: underline;
  cursor: pointer;
`;

const Section = styled.div`
  margin-bottom: 0;
`;

const SectionTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
  font-size: 1.4rem;
  text-align: center;
`;

const ThreeUp = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.medium};

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border || "#e6e6e6"};
  border-radius: 12px;
  background: #fafafa;
  padding: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`;

const CardTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const CardBody = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary || "#666"};
  line-height: 1.4;
`;

const SmallLink = styled(Link)`
  text-decoration: underline;
`;

const ExternalLink = styled.a`
  text-decoration: underline;
`;

const SetupBanner = styled.div`
  border: 1.5px solid ${({ theme }) => theme.colors.border || "#e6e6e6"};
  border-radius: 12px;
  padding: 6px 10px;
  background: #fafafa;
  margin-bottom: 14px;
  display: inline-block;
  font-weight: 600;
`;

const GhostButton = styled.a`
  display: inline-block;
  padding: 0.45rem 1rem;
  border: 1.5px solid #50b4c8;
  border-radius: ${({ theme }) => theme.radii?.small || "6px"};
  color: #50b4c8;
  background: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: #50b4c8;
    color: #ffffff;
  }
`;

const DesktopLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  align-items: start;

  @media (min-width: 820px) {
    grid-template-columns: 1fr auto;
  }
`;

const LeftColumn = styled.div`
  min-width: 0;
`;

const RightColumn = styled.div`
  display: none;

  @media (min-width: 820px) {
    display: block;
    width: 320px;
    flex-shrink: 0;
    position: sticky;
    top: 2rem;
  }
`;

const RightColumnImg = styled.img`
  width: 320px;
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.13);
  display: block;
`;

const MobileScreenshot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;

  @media (min-width: 820px) {
    display: none;
  }
`;

const MobileScreenshotImg = styled.img`
  width: 320px;
  max-width: 100%;
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.13);
  display: block;
`;

const ScreenshotCaption = styled.p`
  margin: ${({ theme }) => theme.spacing.small} 0 0;
  font-size: 0.82rem;
  color: #999;
  text-align: center;
`;

const SocialProof = styled.div`
  background: #50b4c8;
  border-radius: 10px;
  padding: 14px 18px;
  text-align: center;
  margin-bottom: 0;
  font-size: 0.95rem;
  color: #ffffff;
  box-sizing: border-box;
`;

const SocialProofQuote = styled.p`
  margin: 8px 0 0;
  font-size: 0.85rem;
  font-style: italic;
  color: #ffffff;
`;

const TwoUpImgCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionScreenshotImg = styled.img`
  max-width: 280px;
  width: 100%;
  display: block;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border || "#e6e6e6"};
  margin: 2rem 0;
`;

const ScreenshotFrame = styled.div`
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.13);
  border-radius: 18px;
  overflow: hidden;
  display: inline-block;
`;

const Footer = styled.footer`
  text-align: center;
  font-size: 0.82rem;
  color: #999;
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const Landing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  return (
    <Layout>
      <InstallBanner />
      <PageContainer>

        {/* Two-column layout: left=content, right=pinned screenshot */}
        <DesktopLayout>
          <LeftColumn>

            {/* Hero */}
            <Hero>
              <HeroTitle>Fantasy football for your real team.</HeroTitle>
              <HeroSubtitle>Your Sunday league. Your teammates. Your title race.</HeroSubtitle>

              <CTA>
                {currentUser ? (
                  <PrimaryButton onClick={() => navigate("/home")}>Go to My Leagues</PrimaryButton>
                ) : (
                  <>
                    <PrimaryButton onClick={() => navigate("/signup")}>Sign up</PrimaryButton>
                    <SecondaryButton onClick={() => navigate("/login")}>Log in</SecondaryButton>
                    <GhostButton as="button" onClick={() => navigate("/join")}>Join a league</GhostButton>
                  </>
                )}
                {MARKETING_DECK_URL ? (
                  <SecondaryButton as="a" href={MARKETING_DECK_URL} target="_blank" rel="noopener noreferrer">
                    View overview deck
                  </SecondaryButton>
                ) : null}
              </CTA>

              {SETUP_FORM_URL ? (
                <SetupNudge href={SETUP_FORM_URL} target="_blank" rel="noopener noreferrer">
                  Need help getting started? We'll set up your league for you →
                </SetupNudge>
              ) : null}
            </Hero>

            {/* Mobile-only screenshot (hidden on desktop) */}
            <MobileScreenshot>
              <MobileScreenshotImg src={`${process.env.PUBLIC_URL}/images/PitchViewPoints.PNG`} alt="Pitch view with points" />
              <ScreenshotCaption>Pick your teammates, score points, and settle the weekly debate</ScreenshotCaption>
            </MobileScreenshot>

            <Divider />

            {/* Social proof */}
            <SocialProof>
              Trusted by 5+ Sunday league teams and counting
              <SocialProofQuote>"Checking the leaderboard after a game is now more exciting than the match itself" — Charlie, Lower East End FC</SocialProofQuote>
            </SocialProof>

            <Divider />

            {/* How it works */}
            <Section>
              <SectionTitle>How it works</SectionTitle>
              <ThreeUp>
                <Card>
                  <CardTitle>1) Join or create a league</CardTitle>
                  <CardBody>
                    One person sets up the league. Everyone else joins with a code and starts playing
                  </CardBody>
                  {currentUser ? (
                    <>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12, marginBottom: 12 }}>
                        <SecondaryButton onClick={() => navigate("/join")}>Join League</SecondaryButton>
                        <PrimaryButton onClick={() => navigate("/create")}>Create New League</PrimaryButton>
                      </div>
                      {SETUP_FORM_URL ? (
                        <SetupBanner style={{ marginTop: 0, marginBottom: 0 }}>
                          <ExternalLink href={SETUP_FORM_URL} target="_blank" rel="noopener noreferrer">
                            Want me to set up your league? <br />
                            Fill in the setup form
                          </ExternalLink>
                        </SetupBanner>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {SETUP_FORM_URL ? (
                        <SetupBanner style={{ marginTop: 12, marginBottom: 0 }}>
                          <ExternalLink href={SETUP_FORM_URL} target="_blank" rel="noopener noreferrer">
                            Want me to set up your league? <br />
                            Fill in the setup form
                          </ExternalLink>
                        </SetupBanner>
                      ) : null}
                      <p style={{ marginTop: 8, marginBottom: 0, fontSize: "0.8rem", color: "#888888" }}>
                        You'll still need to create an account to join
                      </p>
                    </>
                  )}
                </Card>
                <Card>
                  <CardTitle>2) Pick a weekly team</CardTitle>
                  <CardBody>Pick your squad before the deadline. Your picks lock at kickoff</CardBody>
                </Card>
                <Card>
                  <CardTitle>3) Points update automatically</CardTitle>
                  <CardBody>After each match, scores are calculated and the leaderboard updates instantly</CardBody>
                </Card>
              </ThreeUp>
            </Section>

          </LeftColumn>

          {/* Right column — pinned screenshot, desktop only */}
          <RightColumn>
            <RightColumnImg src={`${process.env.PUBLIC_URL}/images/PitchViewPoints.PNG`} alt="Pitch view with points" />
            <ScreenshotCaption style={{ maxWidth: 320 }}>Pick your teammates, score points, and settle the weekly debate</ScreenshotCaption>
          </RightColumn>

        </DesktopLayout>

        <Divider />

        {/* Screenshots section — full width */}
        <Section>
          <SectionTitle>Your league. Your stats. Your history.</SectionTitle>
          <TwoUp style={{ marginTop: "1.5rem" }}>
            <TwoUpImgCol>
              <ScreenshotFrame>
                <SectionScreenshotImg src={`${process.env.PUBLIC_URL}/images/Leaderboard.PNG`} alt="League leaderboard" />
              </ScreenshotFrame>
            </TwoUpImgCol>
            <TwoUpImgCol>
              <ScreenshotFrame>
                <SectionScreenshotImg src={`${process.env.PUBLIC_URL}/images/PlayerCardPoints.PNG`} alt="Player card with points" />
              </ScreenshotFrame>
            </TwoUpImgCol>
          </TwoUp>
          <ScreenshotCaption style={{ marginTop: 12 }}>
            League standings, player stats, and season history — all in one place
          </ScreenshotCaption>
        </Section>

        <Divider />

        {/* Footer */}
        <Footer>
          <SmallLink to="/faq">FAQ</SmallLink>
          <span>·</span>
          <SmallLink to="/help">Help / Contact</SmallLink>
        </Footer>

      </PageContainer>
    </Layout>
  );
};

export default Landing;
